import React, { Component, useState } from "react";
import { Input } from "reactstrap";
import { toast } from "react-toastify";
import {
  getCrossSellRule,
  createCrossSellRule,
  updateCrossSellRule,
  getFoodList,
  getFoodAddOnList,
  getTransportServicesAdmin,
  getTableList,
  getRoomTypeList,
  getFoodCuisineList,
  getCouponList,
  getBranchList,
} from "../../Endpoint/Admin/index";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import { C, Panel, Btn, Field, Toggle, Chip } from "../../components/Console/ConsoleUI";
import { canWrite, isBranchScopedAdmin, getUserBranchName } from "../../services/PermissionService";
import { HOTEL_UTC_OFFSET, hotelYmd } from "../../utils/period";
import { CONTEXT_GROUPS, KINDS, KIND_LABEL, DAYS, PLACE_TYPES, itemName, scheduleSummary } from "./crossSellMeta";

/**
 * Cross-sell rule editor — one page, in the coupon editor's shape: the rule on
 * the left, a guest-app preview and a plain-words summary on the right.
 *
 * The server validates and normalises every field (controllers/crossSell.js,
 * normaliseRule); the checks here only catch what can be caught before the
 * round trip, with the same wording, and a 400 shows the server's message.
 *
 * On save every field is sent. An emptied field goes as "" (or []), which the
 * server reads as "clear it" — leaving a key out would keep the old value.
 */

const CSS = `
.xs-editor .form-control,.xs-editor .custom-select{height:46px;padding:0 14px;
  background:${C.surface};border:1px solid ${C.border};border-radius:12px;font-size:15px;
  color:${C.ink};box-shadow:none}
.xs-editor .form-control:focus{background:#fff;border-color:${C.sand};box-shadow:none}
.xs-editor .form-control::placeholder{color:${C.taupeLight}}
.xs-chip{padding:8px 15px;border-radius:18px;font-size:14px;font-family:inherit;cursor:pointer;
  background:#fff;border:1px solid ${C.border};color:${C.taupe};line-height:1.3}
.xs-chip.is-on{background:${C.brown};border-color:transparent;color:#fff}
.xs-chip:disabled{cursor:default;opacity:.6}
.xs-list{max-height:220px;overflow:auto;margin-top:10px;border:1px solid ${C.tint};border-radius:12px}
.xs-row{display:flex;align-items:center;gap:10px;width:100%;padding:9px 12px;border:0;
  border-bottom:1px solid ${C.panel};background:#fff;font-size:14px;color:${C.ink};
  font-family:inherit;text-align:left;cursor:pointer}
.xs-row:last-child{border-bottom:0}
.xs-row:hover{background:${C.surface}}
`;

const SelChip = ({ on, onClick, children, disabled }) => (
  <button type="button" className={`xs-chip${on ? " is-on" : ""}`} onClick={onClick} disabled={disabled}>
    {children}
  </button>
);

const Sub = ({ label, hint, children }) => (
  <div style={{ marginTop: 18 }}>
    <div style={{ fontSize: 12, letterSpacing: ".6px", textTransform: "uppercase", color: C.taupe, marginBottom: 9 }}>
      {label}
    </div>
    {children}
    {hint && <div style={{ marginTop: 8, fontSize: 12, color: C.taupeLight }}>{hint}</div>}
  </div>
);

const toggleIn = (list, value) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
const idsOf = (list) => (list || []).map((x) => String(x && x._id ? x._id : x));

/**
 * Searchable multi-pick over one catalog. Selected items sit as chips above
 * the search; an id that is no longer in the catalog still shows (and stays
 * selected) so saving never drops it silently.
 */
const ItemPicker = ({ rows, kind, selected, onChange, disabled, placeholder = "Search" }) => {
  const [query, setQuery] = useState("");
  const byId = new Map(rows.map((r) => [String(r._id), r]));
  const q = query.trim().toLowerCase();
  const matches = rows
    .filter((r) => !selected.includes(String(r._id)))
    .filter((r) => !q || itemName(kind, r).toLowerCase().includes(q))
    .slice(0, 60);
  return (
    <div>
      {selected.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
          {selected.map((id) => (
            <button
              key={id}
              type="button"
              className="xs-chip is-on"
              disabled={disabled}
              onClick={() => onChange(selected.filter((x) => x !== id))}
              title="Remove"
            >
              {byId.has(id) ? itemName(kind, byId.get(id)) : "Item not in this list"} ✕
            </button>
          ))}
        </div>
      )}
      {!disabled && (
        <React.Fragment>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={placeholder} />
          <div className="xs-list">
            {matches.length ? (
              matches.map((r) => (
                <button key={r._id} type="button" className="xs-row" onClick={() => onChange([...selected, String(r._id)])}>
                  <span className="ms" style={{ fontSize: 18, color: C.taupeLight }}>add</span>
                  {itemName(kind, r)}
                </button>
              ))
            ) : (
              <div style={{ padding: "12px 14px", fontSize: 13, color: C.taupeLight }}>
                {rows.length ? "Nothing else matches" : "Nothing to pick from yet"}
              </div>
            )}
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

const HHMM = /^([01]?\d|2[0-3]):[0-5]\d$/;

const EMPTY = {
  name: "",
  priority: "0",
  active: true,
  branchIds: [],
  contexts: [],
  kind: "food",
  refIds: [],
  title: "",
  subtitle: "",
  image: "",
  // none | coupon | percentage | fixed — the offer carries a coupon OR a discount.
  discountMode: "none",
  couponId: "",
  discountValue: "",
  roomTypeIds: [],
  foodIds: [],
  cuisineIds: [],
  placeTypes: [],
  minCartTotal: "",
  hasActiveStay: "", // "" = either, "true" / "false"
  days: [],
  fromTime: "",
  toTime: "",
  startsAt: "", // hotel calendar days, YYYY-MM-DD
  endsAt: "",
};

class CrossSellRuleForm extends Component {
  constructor(props) {
    super(props);
    const id = props.match && props.match.params && props.match.params.id;
    this.state = {
      ...EMPTY,
      ruleId: id || null,
      isLoading: !!id,
      saving: false,
      catalog: { food: [], addon: [], transport_service: [], table: [], room_type: [] },
      cuisines: [],
      coupons: [],
      branches: [],
    };
  }

  componentDidMount() {
    this.loadCatalog();
    if (this.state.ruleId) this.loadRule();
  }

  loadCatalog = async () => {
    const [foods, addons, rides, tables, roomTypes, cuisines, coupons, branches] = await Promise.all([
      getFoodList(),
      getFoodAddOnList(),
      getTransportServicesAdmin(),
      getTableList(),
      getRoomTypeList(),
      getFoodCuisineList(),
      getCouponList(),
      isBranchScopedAdmin() ? Promise.resolve(null) : getBranchList(),
    ]);
    const list = (res) => (res && Array.isArray(res.data) ? res.data : []);
    this.setState({
      catalog: {
        food: list(foods),
        addon: list(addons),
        transport_service: list(rides),
        table: list(tables),
        room_type: list(roomTypes),
      },
      cuisines: list(cuisines),
      coupons: list(coupons).filter((c) => !c.isDeleted),
      branches: list(branches),
    });
  };

  loadRule = () => {
    getCrossSellRule(this.state.ruleId).then((res) => {
      if (!res || res.status !== 200 || !res.data) {
        toast.error((res && res.message) || "Rule not found");
        this.props.history.push("/crosssell");
        return;
      }
      const r = res.data;
      const offer = r.offer || {};
      const cond = r.conditions || {};
      const sched = r.schedule || {};
      this.setState({
        isLoading: false,
        name: r.name || "",
        priority: String(r.priority || 0),
        active: r.active !== false,
        branchIds: idsOf(r.branchIds),
        contexts: r.contexts || [],
        kind: offer.kind || "food",
        refIds: idsOf(offer.refIds),
        title: offer.title || "",
        subtitle: offer.subtitle || "",
        image: offer.image || "",
        discountMode: offer.discountType || (offer.couponId ? "coupon" : "none"),
        couponId: offer.couponId ? String(offer.couponId) : "",
        discountValue: offer.discountValue != null ? String(offer.discountValue) : "",
        roomTypeIds: idsOf(cond.roomTypeIds),
        foodIds: idsOf(cond.foodIds),
        cuisineIds: idsOf(cond.cuisineIds),
        placeTypes: cond.placeTypes || [],
        minCartTotal: cond.minCartTotal != null ? String(cond.minCartTotal) : "",
        hasActiveStay: cond.hasActiveStay == null ? "" : String(cond.hasActiveStay),
        days: sched.days || [],
        fromTime: sched.fromTime || "",
        toTime: sched.toTime || "",
        startsAt: r.startsAt ? hotelYmd(r.startsAt) : "",
        endsAt: r.endsAt ? hotelYmd(r.endsAt) : "",
      });
    });
  };

  set = (patch) => this.setState(patch);

  /** Same checks as the server, in the same words, before the round trip. */
  validate() {
    const s = this.state;
    if (!s.name.trim()) return "Name is required";
    if (!s.contexts.length) return "Pick at least one place in the app to show the offer";
    if (!s.refIds.length) return "Pick at least one item to offer";
    if (s.priority.trim() && !Number.isInteger(Number(s.priority))) return "Priority must be a whole number";
    if (s.discountMode === "coupon" && !s.couponId) return "Pick the coupon to attach";
    if (s.discountMode === "percentage" || s.discountMode === "fixed") {
      const v = Number(s.discountValue);
      if (!(v > 0)) return "Enter the discount amount";
      if (s.discountMode === "percentage" && v > 100) return "A percentage discount can't exceed 100";
    }
    if (s.minCartTotal.trim() && !(Number(s.minCartTotal) >= 0)) return "Minimum cart total must be 0 or more";
    if ((s.fromTime && !HHMM.test(s.fromTime)) || (s.toTime && !HHMM.test(s.toTime))) return "Times must be HH:mm";
    if (!!s.fromTime !== !!s.toTime) return "Set both a start and an end time, or neither";
    if (s.startsAt && s.endsAt && s.endsAt < s.startsAt) return "The end date is before the start date";
    return null;
  }

  buildBody() {
    const s = this.state;
    const discount = s.discountMode === "percentage" || s.discountMode === "fixed";
    const body = {
      name: s.name.trim(),
      priority: Number(s.priority) || 0,
      active: s.active,
      contexts: s.contexts,
      conditions: {
        roomTypeIds: s.roomTypeIds,
        foodIds: s.foodIds,
        cuisineIds: s.cuisineIds,
        placeTypes: s.placeTypes,
        minCartTotal: s.minCartTotal.trim() === "" ? "" : Number(s.minCartTotal),
        hasActiveStay: s.hasActiveStay === "" ? "" : s.hasActiveStay === "true",
      },
      offer: {
        kind: s.kind,
        refIds: s.refIds,
        title: s.title.trim(),
        subtitle: s.subtitle.trim(),
        image: s.image.trim(),
        couponId: s.discountMode === "coupon" ? s.couponId : "",
        discountType: discount ? s.discountMode : "",
        discountValue: discount ? Number(s.discountValue) : "",
      },
      schedule: { days: s.days, fromTime: s.fromTime, toTime: s.toTime },
      // Whole hotel days: from the first day's midnight to the last day's end.
      startsAt: s.startsAt ? `${s.startsAt}T00:00:00${HOTEL_UTC_OFFSET}` : "",
      endsAt: s.endsAt ? `${s.endsAt}T23:59:59.999${HOTEL_UTC_OFFSET}` : "",
    };
    // A branch admin's rules are pinned to their branch by the server.
    if (!isBranchScopedAdmin()) body.branchIds = s.branchIds;
    return body;
  }

  handleSubmit = () => {
    const error = this.validate();
    if (error) {
      toast.error(error);
      return;
    }
    const { ruleId } = this.state;
    this.setState({ saving: true });
    const call = ruleId ? updateCrossSellRule(ruleId, this.buildBody()) : createCrossSellRule(this.buildBody());
    call.then((res) => {
      this.setState({ saving: false });
      if (res && res.status === 200) {
        toast.success(res.message || "Rule saved");
        this.props.history.push("/crosssell");
      } else {
        toast.error((res && res.message) || "Could not save the rule");
      }
    });
  };

  // --- sections --------------------------------------------------------------

  renderBasics(write) {
    const { name, priority, active, branchIds, branches } = this.state;
    return (
      <Panel title="Rule">
        <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "minmax(0,1fr) 160px", gap: 14 }}>
          <Field label="Name" hint="Only admins see this.">
            <Input value={name} onChange={(e) => this.set({ name: e.target.value })} placeholder="e.g. Fries with every burger" />
          </Field>
          <Field label="Priority" hint="Higher wins a tie.">
            <Input type="number" step="1" value={priority} onChange={(e) => this.set({ priority: e.target.value })} />
          </Field>
        </div>
        <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
          <Toggle on={active} disabled={!write} onClick={() => this.set({ active: !active })} />
          <span style={{ fontSize: 14, color: C.ink }}>{active ? "Live — guests can see it" : "Paused"}</span>
        </div>
        {isBranchScopedAdmin() ? (
          <Sub label="Branches" hint="Rules you create run at your branch only.">
            <Chip tone="neutral">{getUserBranchName() || "Your branch"}</Chip>
          </Sub>
        ) : (
          <Sub label="Branches" hint="“All branches” includes branches opened later.">
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <SelChip on={!branchIds.length} disabled={!write} onClick={() => this.set({ branchIds: [] })}>
                All branches
              </SelChip>
              {branches.map((b) => (
                <SelChip
                  key={b._id}
                  on={branchIds.includes(String(b._id))}
                  disabled={!write}
                  onClick={() => this.set({ branchIds: toggleIn(branchIds, String(b._id)) })}
                >
                  {b.name || b.code}
                </SelChip>
              ))}
            </div>
          </Sub>
        )}
      </Panel>
    );
  }

  renderPlacement(write) {
    const { contexts } = this.state;
    return (
      <Panel title="Where it shows" subtitle="The moments in the guest app where this offer may appear.">
        {CONTEXT_GROUPS.map((g) => (
          <Sub key={g.label} label={g.label}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {g.contexts.map((c) => (
                <SelChip
                  key={c.value}
                  on={contexts.includes(c.value)}
                  disabled={!write}
                  onClick={() => this.set({ contexts: toggleIn(contexts, c.value) })}
                >
                  {c.label}
                </SelChip>
              ))}
            </div>
          </Sub>
        ))}
      </Panel>
    );
  }

  renderOffer(write) {
    const { kind, refIds, title, subtitle, image, discountMode, couponId, discountValue, catalog, coupons } = this.state;
    const k = KIND_LABEL[kind];
    return (
      <Panel title="The offer">
        <Sub label="Offer" hint={kind === "addon" ? "Add-ons are offered on a dish the guest already has in the cart." : null}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {KINDS.map((x) => (
              <SelChip
                key={x.value}
                on={kind === x.value}
                disabled={!write}
                // Items of one kind mean nothing to another, so switching clears them.
                onClick={() => kind !== x.value && this.set({ kind: x.value, refIds: [] })}
              >
                {x.label}
              </SelChip>
            ))}
          </div>
        </Sub>
        <Sub label={`Which ${k.label.toLowerCase()}`} hint="Shown in this order of preference; ones a branch doesn't carry are skipped there.">
          <ItemPicker
            rows={catalog[kind] || []}
            kind={kind}
            selected={refIds}
            disabled={!write}
            onChange={(ids) => this.set({ refIds: ids })}
            placeholder={`Search ${k.label.toLowerCase()}`}
          />
        </Sub>

        <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Headline" hint="Optional — defaults to the item's name.">
            <Input value={title} maxLength={80} onChange={(e) => this.set({ title: e.target.value })} />
          </Field>
          <Field label="Subtitle" hint="Optional — defaults to the item's description.">
            <Input value={subtitle} maxLength={120} onChange={(e) => this.set({ subtitle: e.target.value })} />
          </Field>
        </div>
        <div style={{ marginTop: 14 }}>
          <Field label="Image URL" hint="Optional — defaults to the item's own photo.">
            <Input value={image} onChange={(e) => this.set({ image: e.target.value })} placeholder="https://…" />
          </Field>
        </div>

        <Sub label="Price incentive">
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {[
              { value: "none", label: "None" },
              { value: "percentage", label: "% off" },
              { value: "fixed", label: "$ off" },
              { value: "coupon", label: "Attach a coupon" },
            ].map((o) => (
              <SelChip key={o.value} on={discountMode === o.value} disabled={!write} onClick={() => this.set({ discountMode: o.value })}>
                {o.label}
              </SelChip>
            ))}
          </div>
          {(discountMode === "percentage" || discountMode === "fixed") && (
            <div style={{ marginTop: 12, maxWidth: 220 }}>
              <Field label={discountMode === "percentage" ? "Percent off" : "Amount off (USD)"}>
                <Input
                  type="number"
                  min="0"
                  max={discountMode === "percentage" ? "100" : undefined}
                  value={discountValue}
                  onChange={(e) => this.set({ discountValue: e.target.value })}
                />
              </Field>
            </div>
          )}
          {discountMode === "coupon" && (
            <div style={{ marginTop: 12, maxWidth: 360 }}>
              <Field label="Coupon" hint="The app shows the code with the offer; the coupon's own rules still apply.">
                <Input type="select" value={couponId} onChange={(e) => this.set({ couponId: e.target.value })}>
                  <option value="">Choose a coupon</option>
                  {coupons.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.code}{c.name ? ` — ${c.name}` : ""}{c.isActive === false ? " (paused)" : ""}
                    </option>
                  ))}
                </Input>
              </Field>
            </div>
          )}
        </Sub>
      </Panel>
    );
  }

  renderConditions(write) {
    const { contexts, roomTypeIds, foodIds, cuisineIds, placeTypes, minCartTotal, hasActiveStay, catalog, cuisines } = this.state;
    const roomMoment = contexts.some((c) => ["room_detail", "room_checkout", "room_confirmed", "in_stay", "checkout_day"].includes(c));
    const foodMoment = contexts.some((c) => ["dish", "cart", "food_checkout", "order_delivered"].includes(c));
    return (
      <Panel title="Only show when…" subtitle="All optional. Leave everything empty to show the offer to every guest at the moments above.">
        <Sub label="Guest's stay">
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {[
              { value: "", label: "Either" },
              { value: "true", label: "Has an active stay" },
              { value: "false", label: "No active stay" },
            ].map((o) => (
              <SelChip key={o.value} on={hasActiveStay === o.value} disabled={!write} onClick={() => this.set({ hasActiveStay: o.value })}>
                {o.label}
              </SelChip>
            ))}
          </div>
        </Sub>

        <Sub label="Room type is one of" hint={roomMoment ? null : "Only checked at room moments — none is selected above."}>
          <ItemPicker rows={catalog.room_type} kind="room_type" selected={roomTypeIds} disabled={!write}
            onChange={(ids) => this.set({ roomTypeIds: ids })} placeholder="Search room types" />
        </Sub>

        <Sub label="Dish in view or in the cart is one of" hint={foodMoment ? null : "Only checked at food moments — none is selected above."}>
          <ItemPicker rows={catalog.food} kind="food" selected={foodIds} disabled={!write}
            onChange={(ids) => this.set({ foodIds: ids })} placeholder="Search dishes" />
        </Sub>

        <Sub label="Cuisine is one of">
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {cuisines.length ? cuisines.map((c) => (
              <SelChip key={c._id} on={cuisineIds.includes(String(c._id))} disabled={!write}
                onClick={() => this.set({ cuisineIds: toggleIn(cuisineIds, String(c._id)) })}>
                {c.cuisineName}
              </SelChip>
            )) : <span style={{ fontSize: 13, color: C.taupeLight }}>No cuisines yet</span>}
          </div>
        </Sub>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 200px", gap: 14, alignItems: "end" }}>
          <Sub label="Order type is one of">
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {PLACE_TYPES.map((p) => (
                <SelChip key={p.value} on={placeTypes.includes(p.value)} disabled={!write}
                  onClick={() => this.set({ placeTypes: toggleIn(placeTypes, p.value) })}>
                  {p.label}
                </SelChip>
              ))}
            </div>
          </Sub>
          <Field label="Cart total at least (USD)" style={{ marginTop: 18 }}>
            <Input type="number" min="0" value={minCartTotal} onChange={(e) => this.set({ minCartTotal: e.target.value })} placeholder="Any" />
          </Field>
        </div>
      </Panel>
    );
  }

  renderSchedule(write) {
    const { days, fromTime, toTime, startsAt, endsAt } = this.state;
    return (
      <Panel title="Schedule" subtitle="Hotel time. Leave empty to run every day, all day, from now on.">
        <Sub label="Days" hint="No day picked = every day.">
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {DAYS.map((d) => (
              <SelChip key={d.value} on={days.includes(d.value)} disabled={!write}
                onClick={() => this.set({ days: toggleIn(days, d.value) })}>
                {d.label}
              </SelChip>
            ))}
          </div>
        </Sub>
        <div style={{ marginTop: 18, display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 14 }}>
          <Field label="From time" hint="An end before the start runs overnight.">
            <Input type="time" value={fromTime} onChange={(e) => this.set({ fromTime: e.target.value })} />
          </Field>
          <Field label="To time">
            <Input type="time" value={toTime} onChange={(e) => this.set({ toTime: e.target.value })} />
          </Field>
          <Field label="Starts on">
            <Input type="date" value={startsAt} max={endsAt || undefined} onChange={(e) => this.set({ startsAt: e.target.value })} />
          </Field>
          <Field label="Ends on" hint="Inclusive.">
            <Input type="date" value={endsAt} min={startsAt || undefined} onChange={(e) => this.set({ endsAt: e.target.value })} />
          </Field>
        </div>
      </Panel>
    );
  }

  /** Roughly what the guest sees — the first item, with the rule's overrides. */
  renderPreview() {
    const { kind, refIds, title, subtitle, image, discountMode, discountValue, couponId, coupons, catalog } = this.state;
    const first = (catalog[kind] || []).find((r) => String(r._id) === refIds[0]);
    const headline = title.trim() || (first ? itemName(kind, first) : "Pick an item to offer");
    // Only dishes carry their own photo in these lists (the app picks a room's
    // photo for a room type itself), so other kinds preview the icon.
    const ownPhoto = kind === "food" && first && first.foodImages && first.foodImages[0] ? first.foodImages[0].image : null;
    const photo = image.trim() || ownPhoto || null;
    const coupon = discountMode === "coupon" ? coupons.find((c) => String(c._id) === couponId) : null;
    const badge =
      discountMode === "percentage" && Number(discountValue) > 0 ? `${Number(discountValue)}% off`
        : discountMode === "fixed" && Number(discountValue) > 0 ? `$${Number(discountValue)} off`
        : coupon ? `Code ${coupon.code}` : null;
    return (
      <Panel title="In the app" style={{ padding: 20 }}>
        <div style={{ marginTop: 14, display: "flex", gap: 12, padding: 12, borderRadius: 16, background: C.canvas }}>
          <div
            style={{
              width: 72, height: 72, borderRadius: 12, flexShrink: 0, background: C.tint,
              backgroundImage: photo ? `url("${photo}")` : "none", backgroundSize: "cover", backgroundPosition: "center",
              display: "grid", placeItems: "center",
            }}
          >
            {!photo && <span className="ms" style={{ fontSize: 26, color: C.taupeLight }}>{KIND_LABEL[kind].icon}</span>}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 500, color: C.brownDeep }}>{headline}</div>
            {(subtitle.trim() || (first && first.description)) && (
              <div style={{ marginTop: 3, fontSize: 12, color: C.taupe, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {subtitle.trim() || String(first.description).slice(0, 80)}
              </div>
            )}
            {badge && <div style={{ marginTop: 8 }}><Chip tone="bad">{badge}</Chip></div>}
          </div>
        </div>
        <div style={{ marginTop: 10, fontSize: 12, color: C.taupeLight }}>
          Prices are the branch's own and are filled in by the app.
        </div>
      </Panel>
    );
  }

  renderSummary() {
    const s = this.state;
    const row = (label, value) => (
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
        <span>{label}</span>
        <span style={{ color: C.ink, textAlign: "right" }}>{value}</span>
      </div>
    );
    return (
      <Panel title="Summary" style={{ padding: 20 }}>
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 11, fontSize: 14, color: C.slate }}>
          {row("Offers", `${s.refIds.length} ${s.refIds.length === 1 ? KIND_LABEL[s.kind].one : KIND_LABEL[s.kind].label.toLowerCase()}`)}
          {row("Shown at", s.contexts.length ? `${s.contexts.length} moment${s.contexts.length === 1 ? "" : "s"}` : "Nowhere yet")}
          {row("When", scheduleSummary({ days: s.days, fromTime: s.fromTime, toTime: s.toTime }))}
          {row("Dates", s.startsAt || s.endsAt ? `${s.startsAt || "now"} → ${s.endsAt || "no end"}` : "No end date")}
          {!isBranchScopedAdmin() && row("Branches", s.branchIds.length ? `${s.branchIds.length} selected` : "All branches")}
        </div>
      </Panel>
    );
  }

  render() {
    const { ruleId, isLoading, saving, name } = this.state;
    const write = canWrite();

    if (isLoading) {
      return (
        <ConsolePage title="Edit rule" subtitle="Marketing / Cross-sell">
          <div style={{ padding: "80px 0", textAlign: "center", color: C.taupeLight }}>Loading rule…</div>
        </ConsolePage>
      );
    }

    return (
      <ConsolePage
        title={ruleId ? `Edit rule · ${name}` : "New cross-sell rule"}
        subtitle="Marketing / Cross-sell"
        actions={
          <React.Fragment>
            <Btn kind="quiet" icon="arrow_back" onClick={() => this.props.history.push("/crosssell")}>
              Back to rules
            </Btn>
            {write && (
              <Btn icon="check" onClick={this.handleSubmit} disabled={saving}>
                {saving ? "Saving…" : ruleId ? "Save rule" : "Create rule"}
              </Btn>
            )}
          </React.Fragment>
        }
      >
        <style dangerouslySetInnerHTML={{ __html: CSS }} />
        <div className="xs-editor" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 340px", gap: 20, alignItems: "start" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 18, minWidth: 0 }}>
            {this.renderBasics(write)}
            {this.renderPlacement(write)}
            {this.renderOffer(write)}
            {this.renderConditions(write)}
            {this.renderSchedule(write)}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 18, position: "sticky", top: 100 }}>
            {this.renderPreview()}
            {this.renderSummary()}
          </div>
        </div>
      </ConsolePage>
    );
  }
}

export default CrossSellRuleForm;
